import { useEffect, useMemo, useState } from 'react';
import { Pencil, RefreshCw, Share2, X } from 'lucide-react';
import {
  copyTextToClipboard,
  TableActionMenu,
  type BackofficePageMode,
  type TableActionItem,
} from '@/components/shared';

export interface DashboardActionsMenuProps {
  pageMode: BackofficePageMode;
  onPageModeChange: (pageMode: BackofficePageMode) => void;
  onRefresh: () => void;
  refreshing?: boolean;
}

export function DashboardActionsMenu({
  pageMode,
  onPageModeChange,
  onRefresh,
  refreshing = false,
}: DashboardActionsMenuProps) {
  const [shareLabel, setShareLabel] = useState('Share dashboard');

  useEffect(() => {
    if (shareLabel === 'Share dashboard') {
      return;
    }

    const timeout = window.setTimeout(() => setShareLabel('Share dashboard'), 2_000);
    return () => window.clearTimeout(timeout);
  }, [shareLabel]);

  const actions = useMemo<TableActionItem[]>(() => {
    if (pageMode === 'edit') {
      return [
        {
          id: 'close-edit-mode',
          label: 'Close edit mode',
          icon: X,
          placement: 'menu',
          onSelect: () => onPageModeChange('view'),
        },
      ];
    }

    return [
      {
        id: 'refresh-dashboard',
        label: 'Refresh',
        loadingLabel: 'Refreshing',
        icon: RefreshCw,
        placement: 'fixed-inline',
        isLoading: refreshing,
        onSelect: onRefresh,
      },
      {
        id: 'edit-dashboard',
        label: 'Edit dashboard',
        icon: Pencil,
        placement: 'menu',
        onSelect: () => onPageModeChange('edit'),
      },
      {
        id: 'share-dashboard',
        label: shareLabel,
        icon: Share2,
        placement: 'menu',
        onSelect: () => {
          const shareUrl = `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
          void copyTextToClipboard(shareUrl).then((result) => {
            setShareLabel(
              result === 'copied' ? 'Dashboard link copied!' : 'Could not copy dashboard link'
            );
          });
        },
      },
    ];
  }, [onPageModeChange, onRefresh, pageMode, refreshing, shareLabel]);

  return <TableActionMenu actions={actions} label="Dashboard actions" />;
}
